import { useState } from "react";
import { z, ZodSchema } from "zod";
import isEqual from "lodash.isequal";
import { directUploadWithProgress } from "@shared/uploadFile/directUploadWithProgress";
import { useMutation } from "@tanstack/react-query";
import { USER_SET, usePlatformDispatch } from "@platform//PlatformContext";
import { updateUserProfile } from "../utils/updateUserProfile";
import { normalizeUserData } from "../utils/normalizeUserData";

type Args = {
  onSuccess?: (data: any) => void;
  schema: ZodSchema;
  userRole: string;
  previousValues: any;
};

export function useProfileMutation({
  onSuccess,
  schema,
  userRole,
  previousValues: previousValuesProp,
}: Args) {
  const dispatch = usePlatformDispatch();

  const [previousValues, setPreviousValues] = useState(previousValuesProp);

  return useMutation({
    mutationFn: async (data: z.infer<typeof schema>) => {
      let payload = data;

      const { photo, cv } = data;

      if (photo && !isEqual(photo, previousValues.photo)) {
        const photoUpload = await directUploadWithProgress(
          photo.original,
          {
            file_type: "profile_photo",
          },
          () => {}
        );

        payload.media_id = photoUpload.media_id;

        delete payload.photo;
      }

      if (cv && !isEqual(cv, previousValues.cv)) {
        const cvUpload = await directUploadWithProgress(
          cv.original,
          {
            file_type: "resume",
            content_type: cv.type,
          },
          () => {}
        );

        payload.resume_media_id = cvUpload.media_id;

        delete payload.cv;
      }

      // timezone is stored as an array with one element in the Select component
      payload.timezone = payload.timezone ? payload.timezone[0] : undefined;

      return updateUserProfile({ data: payload, role: userRole });
    },
    onError: (error: Error) => {
      throw error;
    },
    onSuccess: (data) => {
      setPreviousValues(normalizeUserData(data.user_data));

      // dispatch update user data
      dispatch({ type: USER_SET, payload: { user: data.user_data } });

      // submit callback
      onSuccess?.(data);
    },
  });
}

export default useProfileMutation;
